import * as React from 'react';
import { Dialog } from 'radix-ui';
import { useTranslation } from 'next-i18next';
import { ControlClose } from '@dx-ui/osc-controls';
import cx from 'classnames';
import { Surfaces, useBrandConfig } from '@dx-ui/global-ui-prism';

export type DialogSizes =
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | 'full'
  | 'container';

export type DialogBaseProps = {
  /** The className for the CloseButton to support custom curated sites */
  closeButtonClassName?: string;
  /** The className for the dialog tag */
  dialogClassName?: string;
  /** The className for the dialog overlay tag */
  dialogOverlayClassName?: string;
  /** The className for the dialog title tag */
  dialogTitleClassName?: string;
  /** The element that opens the dialog that focus should return to */
  dialogTrigger?: React.ReactNode;
  /** The size of the modal. */
  size?: DialogSizes;
  /** Allows passing of additional JSX to dialog. */
  bottomContent?: React.ReactNode;
  /** Whether the overlay is open by default (controlled). */
  isOpen?: boolean;
  /** Handler that is called when the overlay is dismissed. */
  onDismiss?: () => void;
  /** Sets whether header should be sticky in overflow scroll situations when a title is passed. Defaults to false */
  stickyHeader?: boolean;
  /** Optional data attribute to apply to the outer content wrapper */
  contentDataAttribute?: string;
  /** The type of surface to use for the dialog. */
  surfaceType?: Surfaces;
  /** Optional allback for when isOpen changes */
  onOpenChange?: (open: boolean) => void;
  /** When true, sets aria-label directly on the dialog content element for screen reader announcement. Defaults to false. */
  useContentAriaLabel?: boolean;
} & React.ComponentProps<'div'>;

export type DialogOptionalProps =
  | { ariaLabel?: never; title: string; hasChildrenWithTitle?: never }
  | { ariaLabel: string; title?: never; hasChildrenWithTitle?: never }
  | { ariaLabel?: never; title?: never; hasChildrenWithTitle: boolean };

export const DialogTitle = Dialog.Title;
// Track elements in memory instead of decorating the DOM directly
const focusPatchedNodes = new WeakSet<Element>();

export const DialogBase = ({
  ariaLabel,
  children,
  className,
  closeButtonClassName,
  dialogClassName,
  dialogOverlayClassName,
  dialogTitleClassName,
  dialogTrigger,
  isOpen,
  title,
  size = 'md',
  bottomContent,
  onDismiss,
  hasChildrenWithTitle,
  stickyHeader = false,
  contentDataAttribute,
  surfaceType = 'base',
  ref: forwardedRef,
  onOpenChange,
  useContentAriaLabel = false,
}: React.PropsWithChildren<DialogBaseProps & DialogOptionalProps>) => {
  const innerRef = React.useRef<React.ComponentRef<'div'>>(null);
  const dialogRef = React.useRef<React.ComponentRef<'div'>>(null);
  const brandConfig = useBrandConfig();
  const isSurfaceEnabled = brandConfig.surfaceConfig.isEnabled;

  React.useImperativeHandle(forwardedRef, () => dialogRef.current as HTMLDivElement);

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      {dialogTrigger ? <Dialog.Trigger asChild>{dialogTrigger}</Dialog.Trigger> : null}
      <Dialog.Portal>
        <Dialog.Overlay className={cx('dialog-overlay sm:py-4 lg:py-12', dialogOverlayClassName)}>
          <Dialog.Content
            ref={(node: HTMLDivElement | null) => {
              dialogRef.current = node;
              // Replicates the logic from old patch - without actually needing to patch the radix.
              // Prevents aggressive focus stealing when sibling DOM elements unmount.
              if (node && !focusPatchedNodes.has(node)) {
                focusPatchedNodes.add(node);
                // Tracking the last focused element
                let lastActive: Element | null = null;
                node.addEventListener('focusin', (e) => {
                  lastActive = e.target as Element;
                });
                // Intercepting Radix's bad behavior
                const originalFocus = node.focus;
                Object.defineProperty(node, 'focus', {
                  configurable: true,
                  writable: true,
                  value: function (opts?: FocusOptions) {
                    if (document.activeElement === document.body) {
                      if (lastActive && node.contains(lastActive)) {
                        return;
                      }
                    }
                    originalFocus.call(this, opts);
                  },
                });
              }
            }}
            /* This is disabled on purpose because we don't use radix-ui/dialog description. See https://www.radix-ui.com/primitives/docs/components/dialog#description */
            aria-describedby={undefined}
            className={cx(
              'dialog-content-v2 max-h-full rounded-lg sm:max-h-[95vh] md:max-h-[90vh]',
              {
                'max-w-xs': size === 'xs',
                'max-w-sm': size === 'sm',
                'max-w-md': size === 'md',
                'max-w-lg': size === 'lg',
                'max-w-xl': size === 'xl',
                'max-w-2xl': size === '2xl',
                'max-w-3xl': size === '3xl',
                'max-w-4xl': size === '4xl',
                'max-w-5xl': size === '5xl',
                'max-w-6xl': size === '6xl',
                'max-w-full': size === 'full',
                'w-full lg:max-w-[min(90%,calc(1440px-5rem))]': size === 'container',
                'pt-0': stickyHeader,
                'pb-0': bottomContent,
                // TODO: Remove bg-bg and support all surface types on 'dialog-content-v2' when dialog-v2 is migrated to Prism
                'surface-base': isSurfaceEnabled && surfaceType === 'base',
                'surface-alternate': isSurfaceEnabled && surfaceType === 'alternate',
                'surface-inverse': isSurfaceEnabled && surfaceType === 'inverse',
                'surface-contrast': isSurfaceEnabled && surfaceType === 'contrast',
                // oxlint-disable-next-line @nx/workspace/no-legacy-prism-classes
                'bg-bg': !isSurfaceEnabled,
              },
              dialogClassName
            )}
            onPointerDownOutside={() => onDismiss?.()}
            onEscapeKeyDown={() => onDismiss?.()}
            {...(contentDataAttribute ? { [contentDataAttribute]: true } : null)}
            {...(useContentAriaLabel && !hasChildrenWithTitle
              ? { 'aria-label': title || ariaLabel, 'aria-labelledby': undefined }
              : {})}
          >
            <div className="h-full" ref={innerRef}>
              {hasChildrenWithTitle ? null : title ? (
                <div
                  className={cx('relative mb-2 border-b pb-3.5', {
                    'sticky top-0 z-10 pt-4': stickyHeader,
                    'bg-surface': isSurfaceEnabled && stickyHeader,
                    // TODO: Remove bg-bg and support all surface types on 'dialog-content-v2' when dialog-v2 is migrated to Prism
                    // oxlint-disable-next-line @nx/workspace/no-legacy-prism-classes
                    'bg-bg': !isSurfaceEnabled && stickyHeader,
                    'border-surface': isSurfaceEnabled,
                    // oxlint-disable-next-line @nx/workspace/no-legacy-prism-classes
                    'border-border': !isSurfaceEnabled,
                  })}
                >
                  <Dialog.Title
                    className={cx(
                      'osc-dialog-title mx-4 text-center font-bold',
                      dialogTitleClassName
                    )}
                    data-osc-product="osc-dialog-title"
                  >
                    {title}
                  </Dialog.Title>
                  {onDismiss && (
                    <CloseButton
                      className={closeButtonClassName}
                      onDismiss={onDismiss}
                      stickyHeader={stickyHeader}
                    />
                  )}
                </div>
              ) : (
                <Dialog.Title aria-label={ariaLabel} className="hidden" />
              )}
              {!title && onDismiss ? (
                <div className="relative py-1">
                  <CloseButton onDismiss={onDismiss} stickyHeader={stickyHeader} />
                </div>
              ) : null}
              <div className={cx('p-1', className)}>{children}</div>
              {bottomContent}
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

function CloseButton({
  onDismiss,
  stickyHeader,
  className,
}: Pick<DialogBaseProps, 'onDismiss' | 'stickyHeader'> & { className?: string }) {
  const { t } = useTranslation('osc-dialog');
  return (
    <Dialog.Close asChild>
      <ControlClose
        onClick={onDismiss}
        className={cx(
          'absolute -end-2',
          {
            '-top-0.5': !stickyHeader,
            'top-3': stickyHeader,
          },
          className
        )}
        label={t('close')}
      />
    </Dialog.Close>
  );
}
